import PropTypes from 'prop-types';
import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import { M } from '@dashboard-experience/mastodon';
import T from 'components/T';

const Select = ({
  input,
  options,
  label,
  meta: { touched, error, warning },
  testId,
}) => {
  // The field shows the option last picked, or the first option until then,
  // and not the value redux-form holds (an initial value does not select an
  // option). That is how this field has always behaved.
  const [value, setValue] = React.useState('');
  const selectRef = React.useRef(null);
  const onChangeHandler = e => {
    setValue(e.target.value);
    input.onChange(e);
  };

  React.useEffect(() => {
    const initialValue = selectRef.current?.value;
    if (initialValue) setValue(initialValue);
  }, []);

  // A validation error is a translation key, or `{ message, ...params }` with
  // the params for that translation.
  const { message: errorKey, ...errorParams } =
    error && typeof error !== 'string' ? error : { message: error };
  const invalid = Boolean(touched && errorKey);
  const warn = Boolean(touched && !errorKey && warning);

  return (
    <M.Select
      id={input.name ? input.name.replace('.', '-') : 'select-field'}
      name={input.name}
      ref={selectRef}
      data-testid={testId}
      labelText={label ? <T value={label} /> : ''}
      hideLabel={!label}
      value={value}
      onChange={onChangeHandler}
      onBlur={event => input.onBlur(event)}
      onFocus={input.onFocus}
      invalid={invalid}
      invalidText={
        invalid ? <T value={errorKey} {...errorParams} /> : undefined
      }
      warn={warn}
      warnText={warn ? warning : undefined}
    >
      {options.map((option, i) => (
        <M.Select.Item
          key={`option-${i + 1}`}
          value={option.value}
          text={option.i18n ? i18n.getStr(option.name) : option.name}
        />
      ))}
    </M.Select>
  );
};

Select.propTypes = {
  input: PropTypes.object,
  options: PropTypes.array,
  label: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    warning: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }),
  placeholder: PropTypes.string,
  testId: PropTypes.string,
};

Select.defaultProps = {
  input: {},
  options: [],
  label: '',
  meta: {
    touched: false,
    error: '',
    warning: false,
  },
  placeholder: '',
  testId: '',
};

export default i18n.renderTranslation()(Select);
